// Deterministic total-loss risk model — the third rules-driven pass, and the
// other half of the question claimDecision.ts starts. Claim-or-cash asks who
// pays for the repair; this asks whether there will be a repair at all.
//
// Built exactly like its siblings: no AI, no network, every number and every
// user-facing string sourced from data/total-loss-rules.json, so the same
// answers always produce the same verdict and no two render targets can drift
// apart in wording.
//
// It runs over a FINISHED estimate, because the repair range is its numerator.
// The denominator is the car's actual cash value — the one thing the wizard
// never asks for, and the reason this lives on the results page behind a button
// rather than as another step everyone has to walk through.
//
// The model, in one line:
//
//   ratio = (repair range, incl. expected supplement) / (vehicle value range)
//
// Both sides are ranges, so the ratio is an interval, and the verdict reads the
// interval rather than a midpoint. That makes "borderline" the most common
// answer, which is correct: for most cars near the line, nobody — including the
// adjuster — knows yet. Reporting the tipping-point repair bill is worth more
// than a confident guess.
//
// On the numerator: it deliberately includes the expected supplement, via the
// same plannedRepairRange() the claim-or-cash card uses. Carriers total cars on
// the final post-teardown bill, not the first estimate, so excluding it would
// systematically under-call totals on exactly the crashes — structural, high
// hidden-damage — where totals actually happen. Sharing the helper also means
// the two cards on the results page can never quote different repair figures.
import type {
  ConditionAnswer,
  Estimate,
  MileageBand,
  TotalLossAnswers,
  TotalLossAssessment,
  TotalLossMath,
  TotalLossQuestionId,
  TotalLossStatus,
  VehicleClass,
  VehicleValueRange,
} from "./types";
import { plannedRepairRange } from "./claimDecision";
import rules from "../data/total-loss-rules.json";
import ops from "../data/labor-ops.json";

export const TOTAL_LOSS_RULES_VERSION = rules.version;

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const pctStr = (n: number) => `${Math.round(n * 100)}%`;
const round10 = (n: number) => Math.round(n / 10) * 10;

// Same {placeholder} convention the drivability and claim rulesets use. An
// unknown key is left untouched rather than blanked: a visible "{foo}" in
// staging is a better failure than a silently mangled sentence in production.
function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (m, key: string) => vars[key] ?? m);
}

// Nothing to weigh when the engine fell through to its inspection-only path
// (engine.ts step 6): there's no repair scope, so a ratio would be dividing by
// a number we never computed. Same gate as the claim-or-cash card.
export function totalLossApplies(estimate: Estimate): boolean {
  return estimate.damagedParts.length > 0;
}

// The teaser's own prominence, keyed off the estimate. A repair that can't
// realistically reach any normal car's value shouldn't be introduced with
// language implying the question is urgent — but it still gets asked, because
// "any normal car" isn't every car, and an old one can total on very little.
export function totalLossIsQuiet(estimate: Estimate): boolean {
  return plannedRepairRange(estimate).range.high < rules.salience.quietBelowRepairHigh;
}

// --- Which state's ruleset ------------------------------------------------

// Declared rather than inferred from the JSON: TypeScript reads WA's unverified
// `pct: null` and OR's `pct: 0.8` as incompatible literal types, so deriving the
// shape from one entry can't describe the other.
interface StateRules {
  name: string;
  insurerThresholdPct: number;
  statutory: { verified: boolean; kind: string; pct: number | null; note: string };
}

// Only the two states this directory serves. Anything else falls back to the
// default ruleset — and because the copy only ever names a state it actually
// matched, an out-of-region ZIP degrades to threshold-only wording rather than
// telling someone in Ohio about Washington law.
export function stateFor(zip?: string | null): { code: string; rules: StateRules } | null {
  const prefix = Number((zip ?? "").trim().slice(0, 3));
  if (Number.isFinite(prefix)) {
    for (const [code, prefixes] of Object.entries(rules.zipPrefixes)) {
      if (Array.isArray(prefixes) && (prefixes as number[]).includes(prefix))
        return { code, rules: (rules.states as Record<string, StateRules>)[code] };
    }
  }
  const fallback = (rules.states as Record<string, StateRules>)[rules.defaultState];
  return fallback ? { code: rules.defaultState, rules: fallback } : null;
}

// --- The valuation fallback -----------------------------------------------

const V = rules.valuation;

// The retention curve is sparse (0-10, then 12/15/20/25), so read between its
// points rather than stepping. Straight-line interpolation is more than the
// underlying data deserves, but a stepped curve would put a visible cliff
// between two model years that are worth roughly the same.
function retentionAt(age: number): number {
  const table = V.retentionByAge as Record<string, number>;
  const ages = Object.keys(table)
    .map(Number)
    .sort((a, b) => a - b);
  const clamped = Math.max(0, age);
  if (clamped <= ages[0]) return table[String(ages[0])];
  const last = ages[ages.length - 1];
  if (clamped >= last) return table[String(last)];
  for (let i = 0; i < ages.length - 1; i++) {
    const lo = ages[i];
    const hi = ages[i + 1];
    if (clamped >= lo && clamped <= hi) {
      const t = (clamped - lo) / (hi - lo);
      return table[String(lo)] + t * (table[String(hi)] - table[String(lo)]);
    }
  }
  return table[String(last)];
}

// Class-average new price × age retention × mileage × condition, then widened
// into a deliberately broad band. Every step of this is a class average rather
// than a fact about the user's car, which is why the band is wide and why the
// output always says where the number came from.
function estimateValue(
  estimate: Estimate,
  answers: TotalLossAnswers,
  currentYear: number,
): VehicleValueRange | null {
  const year = estimate.vehicle.year;
  // Without a year there's no curve to sit on, and guessing an age would be
  // inventing the entire answer rather than estimating it.
  if (!year || year < 1900 || year > currentYear + 1) return null;

  const cls: VehicleClass = estimate.pricing?.vehicleClass ?? "mainstream";
  const base = (V.typicalNewPrice as Record<string, number>)[cls] ?? V.typicalNewPrice.mainstream;
  const classMult =
    (V.classRetentionMultiplier as Record<string, number>)[cls] ?? 1;

  const mileage: MileageBand = answers.mileage ?? "unknown";
  const condition: ConditionAnswer = answers.condition ?? "average";
  const mileageMult = (V.mileageMultiplier as Record<string, number>)[mileage] ?? 1;
  const conditionMult = (V.conditionMultiplier as Record<string, number>)[condition] ?? 1;

  const age = Math.max(0, currentYear - year);
  const point = base * retentionAt(age) * classMult * mileageMult * conditionMult;

  // An unknown mileage is the one answer that widens the band rather than
  // moving it: we're not less sure which direction it goes, we're less sure how
  // far. Condition has no unknown — the question always gets an answer.
  const band = mileage === "unknown" ? V.bandPctWhenUnknown : V.bandPct;

  return {
    low: Math.max(V.floor, round10(point * (1 - band))),
    high: Math.max(V.floor + 500, round10(point * (1 + band))),
    currency: "USD",
    source: "estimated",
  };
}

// The value the verdict runs on: whatever the user gave us, or the depreciation
// fallback once they've answered the two follow-ups. Null means "not enough
// answered yet", which is the "unknown" verdict rather than an error.
export function resolveValue(
  estimate: Estimate,
  answers: TotalLossAnswers,
  currentYear: number,
): VehicleValueRange | null {
  if (
    answers.valueSource &&
    answers.valueSource !== "estimated" &&
    answers.valueLow != null &&
    answers.valueHigh != null
  ) {
    return {
      low: answers.valueLow,
      high: answers.valueHigh,
      currency: "USD",
      source: answers.valueSource,
    };
  }
  if (answers.valueSource !== "estimated") return null;
  if (!answers.mileage || !answers.condition) return null;
  return estimateValue(estimate, answers, currentYear);
}

// --- Question flow ---------------------------------------------------------

// One question for anyone who knows what their car is worth. The two follow-ups
// exist only on the "estimate it for me" branch, which is the branch that needs
// them — a value they gave us directly already carries its own uncertainty.
export function nextTotalLossQuestion(answers: TotalLossAnswers): TotalLossQuestionId | null {
  if (!answers.valueSource) return "value";
  if (answers.valueSource !== "estimated") return null;
  if (!answers.mileage) return "mileage";
  if (!answers.condition) return "condition";
  return null;
}

export const TOTAL_LOSS_QUESTION_ORDER: TotalLossQuestionId[] = ["value", "mileage", "condition"];

// Clear one answer and everything downstream of it — the follow-ups are only
// reachable through the value question, so keeping them would leave a stale
// mileage answer feeding a value the user just re-stated.
export function resetTotalLossFrom(
  answers: TotalLossAnswers,
  q: TotalLossQuestionId,
): TotalLossAnswers {
  const next: TotalLossAnswers = { ...answers };
  if (q === "value") {
    delete next.valueLow;
    delete next.valueHigh;
    delete next.valueSource;
  }
  if (q === "value" || q === "mileage") delete next.mileage;
  delete next.condition;
  return next;
}

// --- The verdict -----------------------------------------------------------

// Band the MIDPOINT against a zone around the threshold. Reading the raw
// interval instead was the obvious first move and it was wrong: with ranges on
// both sides of the ratio, nearly every case straddles the line, so a repair
// sitting at 42% of the car's value and one sitting at 226% both came out
// "genuinely close to it" — a label that was useless in one direction and
// dangerously reassuring in the other.
//
// The interval still does work, just not this work: when it crosses a line the
// midpoint stayed clear of, the caller adds rangeReachesLine/rangeStaysBelow.
// That puts the uncertainty in a caveat, where it reads as uncertainty, rather
// than in the headline, where it read as a verdict.
function statusFor(ratioMid: number, threshold: number): TotalLossStatus {
  const b = rules.banding;
  if (ratioMid >= b.exceedsRatio) return "exceeds";
  if (ratioMid > threshold * b.borderlineHighMult) return "likely";
  if (ratioMid < threshold * b.borderlineLowMult) return "unlikely";
  return "borderline";
}

// "Structural" in the sense that moves a total-loss decision: frame-rate work
// or a WELDED structural panel, i.e. the vehicle goes on a rack. Deliberately
// the same test the pricing engine applies before it bills bench setup — a
// bolt-on reinforcement bar carries the "struct" category but unbolts, and
// counting it here would flag half the bumper jobs as structural.
const isStructuralPart = (id: string): boolean => {
  const p = (
    ops.parts as unknown as Record<
      string,
      { category: string; laborType: string; attachment?: string } | undefined
    >
  )[id];
  if (!p) return false;
  return p.laborType === "frame" || (p.category === "struct" && p.attachment === "welded");
};

export interface TotalLossOptions {
  // ZIP the session was run for, used only to pick a state ruleset.
  zip?: string | null;
  // Injectable so the depreciation curve is testable and a report can be
  // re-rendered against the year it was generated in.
  currentYear?: number;
}

export function assessTotalLoss(
  estimate: Estimate,
  answers: TotalLossAnswers,
  opts: TotalLossOptions = {},
): TotalLossAssessment {
  const copy = rules.copy;
  const base = { rulesVersion: rules.version, disclaimer: copy.disclaimer };
  const currentYear = opts.currentYear ?? new Date().getFullYear();

  const value = resolveValue(estimate, answers, currentYear);
  if (!value) {
    const v = copy.verdicts.unknown;
    return {
      ...base,
      status: "unknown",
      headline: v.headline,
      summary: v.summary,
      points: [],
      nextStep: v.nextStep,
    };
  }

  const st = stateFor(opts.zip);
  const threshold = st?.rules.insurerThresholdPct ?? 0.75;

  const planned = plannedRepairRange(estimate);
  const repair = planned.range;

  // Interval arithmetic: the cheapest repair against the most valuable car is
  // the best case, the dearest repair against the least valuable car the worst.
  const ratioRange = {
    low: repair.low / Math.max(1, value.high),
    high: repair.high / Math.max(1, value.low),
  };
  const midValue = (value.low + value.high) / 2;
  const ratioMid = (repair.low + repair.high) / 2 / Math.max(1, midValue);

  const statutory = st?.rules.statutory;
  const math: TotalLossMath = {
    repairRange: repair,
    supplementIncluded: planned.supplement,
    value,
    ratioRange,
    ratioMid,
    insurerThresholdPct: threshold,
    // Only a figure that has been checked against statute reaches the reader.
    // The ruleset ships with verified:false on purpose — see verificationTodo.
    ...(statutory?.verified && typeof statutory.pct === "number"
      ? { statutoryThresholdPct: statutory.pct, stateName: st?.rules.name }
      : {}),
    thresholdDollars: round10(midValue * threshold),
  };

  const status = statusFor(ratioMid, threshold);
  const v = copy.verdicts[status as Exclude<TotalLossStatus, "unknown">];

  const vars: Record<string, string> = {
    ratioMid: pctStr(ratioMid),
    ratioLow: pctStr(ratioRange.low),
    ratioHigh: pctStr(ratioRange.high),
    thresholdPct: pctStr(threshold),
    thresholdDollars: usd(math.thresholdDollars),
    repairLow: usd(repair.low),
    repairHigh: usd(repair.high),
    valueLow: usd(value.low),
    valueHigh: usd(value.high),
    valueExact: usd(value.low),
    supplementLow: planned.supplement ? usd(planned.supplement.low) : "",
    supplementHigh: planned.supplement ? usd(planned.supplement.high) : "",
    hiddenBand: estimate.hiddenDamage.band,
  };

  const m = copy.modifiers;
  const points: string[] = v.points.map((t) => fill(t, vars));

  // Where the value came from, first — it's the biggest caveat on the whole
  // card, and on the estimated path it's the number most likely to be wrong.
  if (value.source === "estimated") points.push(fill(m.estimatedValue, vars));
  else if (value.source === "user-exact") points.push(fill(m.userValueExact, vars));
  else points.push(fill(m.userValue, vars));

  // What the midpoint verdict left out. The interval is wide enough that it
  // regularly reaches past the line the midpoint sits clear of, and a reader
  // told "unlikely" deserves to know their worst case still touches it.
  if (status === "unlikely" && ratioRange.high >= threshold)
    points.push(fill(m.rangeReachesLine, vars));
  if ((status === "likely" || status === "exceeds") && ratioRange.low < threshold)
    points.push(fill(m.rangeStaysBelow, vars));

  // Then the things that move the ratio, in rough order of how much they move it.
  if (planned.supplement) points.push(fill(m.supplementIncluded, vars));
  if (estimate.hiddenDamage.band === "high") points.push(fill(m.hiddenDamage, vars));
  if (estimate.damagedParts.some((p) => isStructuralPart(p.part)))
    points.push(m.structural);
  if (estimate.vehicle.year && currentYear - estimate.vehicle.year >= 12)
    points.push(m.oldVehicle);
  // Costs the carrier counts and we don't. Last of the modifiers because it
  // only ever nudges, but it belongs above the always-on notes because it's
  // about this estimate rather than about total losses in general.
  if (status !== "unlikely") points.push(m.notCounted);

  return {
    ...base,
    status,
    headline: v.headline,
    summary: fill(v.summary, vars),
    math,
    points: [...points, ...copy.always],
    nextStep: fill(v.nextStep, vars),
  };
}

// Copy exported for the UI layer, same as the claim ruleset: the teaser text
// and question prompts live in the dataset too, so nothing has to be restated
// in a component.
export const TOTAL_LOSS_COPY = rules.copy;
export const TOTAL_LOSS_QUESTIONS = rules.questions;
